import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { RegolePassword, REGOLE_PASSWORD } from "@/pages/CambiaPassword";

const inp = "w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm bg-white text-slate-800 " +
  "focus:outline-none focus:ring-2 focus:ring-sky-300 transition-colors duration-200";
const lbl = "block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1";
const card = "w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-7";
const wrap = "min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4";

function Logo() {
  return (
    <div className="flex flex-col items-center mb-8">
      <img src="/dbe-logo.png" alt="DBE Consulenza Finanziaria" className="h-20 w-auto" />
    </div>
  );
}

/** Richiesta del link di reset via email. */
export function PasswordDimenticata() {
  const [email, setEmail] = useState("");
  const [inviata, setInviata] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password-dimenticata", { email: email.trim() });
      setInviata(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={wrap} data-testid="password-dimenticata-page">
      <div className="w-full max-w-md">
        <Logo />
        <div className={card}>
          <h1 className="text-2xl font-semibold text-slate-900">Password dimenticata</h1>
          {inviata ? (
            <p className="text-sm text-slate-600 mt-3" data-testid="pd-conferma">
              Se l'indirizzo è registrato riceverai a breve un'email con il link per impostare
              una nuova password. Il link è valido 30 minuti.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <p className="text-sm text-slate-500">
                Inserisci l'email del tuo account: ti invieremo un link per reimpostare la password.
              </p>
              <div>
                <label className={lbl}>Email</label>
                <input className={inp} type="email" value={email} data-testid="pd-email"
                  onChange={(e) => setEmail(e.target.value)} placeholder="nome@agenzia-dbeasy.it" />
              </div>
              <button type="submit" disabled={busy || !email} data-testid="pd-invia"
                className="w-full px-5 py-2.5 rounded-md bg-sky-600 text-white text-sm font-semibold
                  hover:bg-sky-700 disabled:opacity-50 transition-colors duration-200">
                {busy ? "Invio…" : "Invia il link"}
              </button>
            </form>
          )}
          <Link to="/" data-testid="pd-torna-login"
            className="block text-center text-xs text-slate-400 hover:text-slate-700 mt-6 transition-colors duration-200">
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Impostazione della nuova password dal link ricevuto per email. */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [conferma, setConferma] = useState("");
  const [fatto, setFatto] = useState(false);
  const [busy, setBusy] = useState(false);
  const valida = REGOLE_PASSWORD.every((r) => r.test(pw)) && pw === conferma;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password_nuova: pw });
      setFatto(true);
      toast.success("Password aggiornata: ora puoi accedere");
      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={wrap} data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Logo />
        <form onSubmit={submit} className={card}>
          <h1 className="text-2xl font-semibold text-slate-900">Nuova password</h1>
          {!token ? (
            <p className="text-sm text-red-600 mt-3" data-testid="rp-token-mancante">
              Link non valido: richiedi un nuovo link di reset dalla pagina di login.
            </p>
          ) : fatto ? (
            <p className="text-sm text-emerald-700 mt-3" data-testid="rp-conferma">
              Password aggiornata. Ti stiamo riportando al login…
            </p>
          ) : (
            <div className="mt-6 space-y-4">
              <div>
                <label className={lbl}>Nuova password</label>
                <input className={inp} type="password" value={pw} data-testid="rp-password"
                  onChange={(e) => setPw(e.target.value)} />
                <RegolePassword valore={pw} />
              </div>
              <div>
                <label className={lbl}>Conferma password</label>
                <input className={inp} type="password" value={conferma} data-testid="rp-conferma-input"
                  onChange={(e) => setConferma(e.target.value)} />
                {conferma && conferma !== pw && (
                  <p className="text-xs text-red-600 mt-1">Le due password non coincidono</p>
                )}
              </div>
              <button type="submit" disabled={!valida || busy} data-testid="rp-salva"
                className="w-full px-5 py-2.5 rounded-md bg-sky-600 text-white text-sm font-semibold
                  hover:bg-sky-700 disabled:opacity-50 transition-colors duration-200">
                {busy ? "Salvataggio…" : "Imposta la password"}
              </button>
            </div>
          )}
          <Link to="/" className="block text-center text-xs text-slate-400 hover:text-slate-700 mt-6
            transition-colors duration-200">Torna al login</Link>
        </form>
      </div>
    </div>
  );
}
